import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { BUSINESS } from "../data/site";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/timbertech", label: "TimberTech" },
  { to: "/gallery", label: "Gallery" },
  { to: "/financing", label: "Financing" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all ${
        scrolled ? "bg-white/95 backdrop-blur border-b border-[#E5E5E5]" : "bg-white border-b border-transparent"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-20 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center group"
        >
          <img
            src="/logo.png"
            alt="Zarco's Decks — Custom Decks Built Strong, Built Right"
            data-testid="nav-logo-img"
            className="h-14 md:h-16 w-auto"
          />
        </Link>

        <nav className="hidden xl:flex items-center gap-6">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[13px] font-medium tracking-wide whitespace-nowrap transition-colors ${
                  isActive ? "text-[#99582A]" : "text-[#111] hover:text-[#99582A]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-8 shrink-0 ml-6">
          <a
            href={BUSINESS.phoneMainHref}
            data-testid="nav-phone"
            className="flex items-center gap-2 text-[13px] font-medium whitespace-nowrap text-[#111] hover:text-[#99582A] transition-colors"
          >
            <Phone size={16} strokeWidth={1.75} />
            {BUSINESS.phoneMain}
          </a>
          <Link
            to="/contact"
            data-testid="nav-free-estimate-btn"
            className="bg-[#111] text-white hover:bg-[#99582A] transition-colors px-7 py-3 text-sm font-semibold tracking-wide whitespace-nowrap"
          >
            Get Your Free Estimate
          </Link>
        </div>

        <button
          onClick={() => setOpen((s) => !s)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          className="xl:hidden p-2 text-[#111]"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {/* Mobile drawer — full-height solid panel so page CTAs aren't tappable behind it */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="xl:hidden fixed inset-x-0 bottom-0 top-[73px] z-[60] bg-white border-t border-[#E5E5E5] px-6 py-6 space-y-1 overflow-y-auto"
        >
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `block py-3 text-lg font-display font-medium ${
                  isActive ? "text-[#99582A]" : "text-[#111]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="mobile-free-estimate-btn"
            className="mt-4 inline-block bg-[#111] text-white px-6 py-3 font-semibold w-full text-center"
          >
            Get Your Free Estimate
          </Link>
          <a
            href={BUSINESS.phoneMainHref}
            className="mt-3 flex items-center justify-center gap-2 border-2 border-[#111] py-3 font-medium"
            data-testid="mobile-phone-link"
          >
            <Phone size={16} /> {BUSINESS.phoneMain}
          </a>
        </div>
      )}
    </header>
  );
}
